import { useMemo, useState } from 'react'
import to from 'await-to-js'

import { Modal } from 'components/UI/Modal'
import { Combobox } from 'components/UI/combobox/combobox'
import { CompanyTypeSlug } from 'models/companies'
import useApiService from 'services/api'
import { clientInstance } from 'utils/http-client'
import { baseAPICall } from 'services/baseAPICall'
import { invalidateCampaignTargetQueries } from 'services/common_table_actions'

import { CreateCampaignModalV2 } from './CreateCampaignModalV2'

// The single-company campaign assignment reuses the company-level bulk assign
// endpoint the data tables already drive (see `assignToCampaign` in
// services/common_table_actions.ts and `assignCompaniesToCampaign` in the
// contact-import wizard). For one company we pass its contact-company id as the
// single `include_ids` entry.
// Refer to server/common/table_actions_mixins/BulkAssignToCampaignMixin.py.
const ASSIGN_ENDPOINT = 'contacts/contactcompanies/campaigns/assign/'

export interface CampaignComboboxOption {
  label: string
  value: number
  color?: string
}

/**
 * Map the campaigns list response into combobox options. Extracted as a pure
 * helper so the mapping is unit-testable without rendering the modal (the
 * repo's vitest config runs in a `node` environment with no jsdom).
 */
export function buildCampaignComboboxOptions(
  campaigns: { id: number; name: string; color?: string }[] | undefined
): CampaignComboboxOption[] {
  return (
    campaigns?.map((c) => ({
      label: c.name,
      value: c.id,
      color: c.color,
    })) ?? []
  )
}

/**
 * Assign a single company (by contact-company id) to a campaign via the same
 * bulk assign endpoint the data tables use. Kept as a plain `clientInstance`
 * call (mirroring `assignCompaniesToCampaign`) because the company detail page
 * has no DataTable optimistic cache for `useAssignToCampaignMutation` to drive.
 */
async function assignCompanyToCampaign(campaignId: number, companyId: number) {
  return baseAPICall(
    () =>
      clientInstance.post(ASSIGN_ENDPOINT, {
        campaign_id: campaignId,
        include_ids: [companyId],
        exclude_ids: [],
        assign: true,
      }),
    { successMessage: 'Successfully added to campaign' }
  )
}

interface AddCompanyToCampaignModalProps {
  show: boolean
  onClose: () => void
  /** Contact-company id of the company being viewed (the assign include id). */
  contactCompanyId: number
  /** Company type, used to pre-fill the type when creating a new campaign. */
  companyTypeSlug?: CompanyTypeSlug
  /** Called after a successful assignment so the page can refresh. */
  onSuccess?: () => void
}

/**
 * "Add to Campaign" affordance for the company detail page (FIR-6177). Surfaces
 * the same add-to-campaign flow the data tables already offer, scoped to the
 * single company in view: pick an existing campaign or create a new one, then
 * assign through the existing company-level assign endpoint.
 */
export function AddCompanyToCampaignModal({
  show,
  onClose,
  contactCompanyId,
  companyTypeSlug,
  onSuccess,
}: AddCompanyToCampaignModalProps) {
  const api = useApiService()
  const { data: campaigns, isLoading } = api.useGetCampaigns()

  const [openNewCampaign, setOpenNewCampaign] = useState(false)
  const [isAssigning, setIsAssigning] = useState(false)

  const comboboxOptions = useMemo(
    () =>
      buildCampaignComboboxOptions(campaigns?.results).map((option) => ({
        label: option.label,
        value: option.value,
        icon: (
          <div
            className="h-3 w-3 rounded-full mr-2 flex-shrink-0"
            style={{ backgroundColor: option.color }}
          />
        ),
      })),
    [campaigns]
  )

  async function handleAssign(campaignId: number | string) {
    setIsAssigning(true)
    const [err] = await to(
      assignCompanyToCampaign(Number(campaignId), contactCompanyId)
    )
    setIsAssigning(false)
    if (err) {
      // baseAPICall already surfaced the error toast.
      return
    }
    invalidateCampaignTargetQueries(Number(campaignId))
    onSuccess?.()
    onClose()
  }

  return (
    <>
      {/* Hide the picker modal while the create-campaign modal is open so the
          two do not stack on top of each other (the create flow re-invokes
          handleAssign on success). */}
      <Modal show={show && !openNewCampaign} onHide={onClose} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-lg font-semibold text-fg">
            Add to Campaign
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-sm text-fg-secondary mb-3">
            Add this company to an existing campaign, or create a new one.
          </p>
          <Combobox
            options={comboboxOptions}
            searchPlaceholder="Search Campaigns..."
            placeholder="Select Campaign"
            onSelectAltAction={handleAssign}
            onAddNew={() => setOpenNewCampaign(true)}
            loading={isLoading || isAssigning}
            buttonLess
          />
        </Modal.Body>
      </Modal>

      <CreateCampaignModalV2
        show={openNewCampaign}
        onHide={() => setOpenNewCampaign(false)}
        defaultTypeSlug={companyTypeSlug}
        onCreated={async (campaign) => {
          setOpenNewCampaign(false)
          await handleAssign(campaign.id)
        }}
      />
    </>
  )
}
